// Maps a caught error to something a learner can actually act on.
//
// Why this exists: `FetchError.message` is built for telemetry, not for people.
// On a network failure it reads:
//
//   HTTP 0 Network error on POST https://tacit.coach/api/auth/capture-email
//
// That exact string reached a guest's screen in production on 2026-08-02
// (Sentry CHATBOT-FRONTEND-4J) — a learner in Abuja whose mobile connection
// dropped while saving their progress. The path was `setSaveProgressError(err.message)`
// straight into the modal.
//
// Callers that surface errors to a learner should route through this instead of
// reading `.message` directly. Errors the backend authored (e.g. the `{ error }`
// payload rethrown by `AuthAPI.captureEmail`) are already learner-appropriate and
// pass through unchanged — only the HTTP-jargon ones get replaced.
//
// NOTE: ~137 call sites still surface a raw `.message`. This helper is the
// intended landing place for that cleanup, not just these two call sites.

import { FetchError } from './safeFetch';

/**
 * @param {unknown} err - the caught error
 * @param {(key: string, opts: object) => string} t - i18next translate fn
 * @param {string} fallback - used when the error carries no usable message
 * @returns {string} a message safe to render to a learner
 */
// #3459 / #3475 / #3487 / #3406: sign-in outcomes that carry a code and need
// a translated, actionable message rather than the English text attached to
// the error. Keyed by code so the three Google sign-in surfaces (LoginPage,
// LoginModal, VerifyPage) and the guest-entry callers stay in step.
const CODED_MESSAGES = {
  GUEST_CONVERSION_UNRESOLVED: 'auth.guestConversionUnresolved',
  GUEST_CONVERSION_SESSION_CHANGED: 'auth.guestConversionSessionChanged',
  ACCOUNT_ORG_CONFLICT: 'auth.accountOrgConflict',
  REAL_SESSION_UNVERIFIED: 'auth.realSessionUnverified',
  // PR #3762 fix round 3 (#3672): a scored, counted attempt on a capped
  // assignment cannot be reopened (409 from PUT /api/runs/:id/status). Not a
  // server problem, so never errors.serverProblem.
  ASSIGNMENT_ATTEMPT_FINAL: 'errors.assignmentAttemptFinal',
  // PR #3762 §3.4 fix round: the attempt cap's 403. Never errors.serverProblem,
  // and never the server's sentence (its counts read "0 used" for a pre-cap run).
  // Post-3.4 override fix: this global copy is START copy ("no attempts left");
  // the reopen paths use errors.assignmentAttemptsExhaustedReopen instead
  // (lib/runs/reopenRun.js reopenRefusalMessage), which also names the
  // started-before-the-limit reason that only a reopen can hit.
  ASSIGNMENT_ATTEMPTS_EXHAUSTED: 'errors.assignmentAttemptsExhausted',
  // #3764: the other two terminal start refusals, with the SAME keys
  // startSessionErrorKey (utils/startSessionErrorMessage.js) uses, so a
  // non-start surface (reopen, end-session on a swept run) never reads
  // "Something went wrong on our side" for a deadline or a pathway lock.
  ASSIGNMENT_CLOSED: 'dashboard.errorAssignmentClosed',
  PATHWAY_SCENARIO_LOCKED: 'dashboard.errorPathwayLocked',
};

// The backend sends `{ error, code }`; a FetchError carries it parsed on
// `.body` (safeFetch.ts, #1000), not on `.code`.
function errorCodeOf(err) {
  if (!err || typeof err !== 'object') return null;
  if (typeof err.code === 'string') return err.code;
  if (err instanceof FetchError && err.body && typeof err.body === 'object' && typeof err.body.code === 'string') {
    return err.body.code;
  }
  return null;
}

export function toUserFacingMessage(err, t, fallback) {
  const code = errorCodeOf(err);
  const codedKey = code ? CODED_MESSAGES[code] : null;
  if (codedKey) {
    // A FetchError's .message is telemetry ("HTTP 409 ..."), never a fallback
    // a learner should read; the server's own `error` text is.
    const serverText = err instanceof FetchError ? err.body?.error : err.message;
    return t(codedKey, { defaultValue: (typeof serverText === 'string' && serverText) || fallback });
  }

  if (err instanceof FetchError) {
    // status 0 = the request never got a response (offline, DNS, dropped
    // radio). Actionable by the learner, so say so.
    if (err.status === 0) {
      return t('errors.connectionLost', {
        defaultValue: "Couldn't reach the server — check your connection and try again.",
      });
    }
    // Any other FetchError is a 5xx or an unhandled status. Not the learner's
    // fault and not their problem to diagnose.
    return t('errors.serverProblem', {
      defaultValue: 'Something went wrong on our side. Please try again in a moment.',
    });
  }

  return (err && err.message) || fallback;
}
